import {
  ComplianceCheckStatusReportEnum,
  IComplianceChecksElementary,
  IComplianceReport,
  IDraftingMediaAssets,
} from "@shared/lib/types/drafting";
import { AlertCircle, Check, Loader2, RefreshCw, X } from "lucide-react";
import styles from "./styles.module.scss";

const GROUPS: { title: string; key: keyof IComplianceReport }[] = [
  { title: "Brand guardrails", key: "brandGuardRailsChecks" },
  { title: "Advanced prompting", key: "advancedPromptingChecks" },
  { title: "Performance", key: "performanceGuidelinesChecks" },
  { title: "Consistency", key: "consistencyChecks" },
  { title: "Safety", key: "safetyChecks" },
  { title: "Script vs draft", key: "scriptVsDraftingConsistencyChecks" },
];

type DraftMediaReportsProps = {
  assets: IDraftingMediaAssets[];
  requestPromptId?: string;
  promptChanged: boolean;
  isRunning: boolean;
  onRerun: (mediaAssetId: string) => void;
};

function assetWasChecked(asset: IDraftingMediaAssets) {
  return Boolean(asset.complianceCheckGeneratedAt || asset.complianceCheckReport);
}

function assetOutdated(
  asset: IDraftingMediaAssets,
  requestPromptId: string | undefined,
  promptChanged: boolean
) {
  if (!assetWasChecked(asset)) return false;
  const idChanged =
    Boolean(requestPromptId) &&
    Boolean(asset.aiWorkspaceMasterPromptId) &&
    requestPromptId !== asset.aiWorkspaceMasterPromptId;
  return promptChanged || idChanged;
}

function statusClass(status?: string) {
  if (status === ComplianceCheckStatusReportEnum.Compliant) return styles.statusPass;
  if (status === ComplianceCheckStatusReportEnum.PartiallyCompliant) return styles.statusWarn;
  if (status === ComplianceCheckStatusReportEnum.NonCompliant) return styles.statusFail;
  return styles.statusNeutral;
}

function CheckRow({ check }: { check: IComplianceChecksElementary }) {
  const icon =
    check.status === ComplianceCheckStatusReportEnum.Compliant ? (
      <Check size={12} />
    ) : check.status === ComplianceCheckStatusReportEnum.PartiallyCompliant ? (
      <AlertCircle size={12} />
    ) : (
      <X size={12} />
    );

  return (
    <li className={styles.checkRow}>
      <span className={`${styles.checkIcon} ${statusClass(check.status)}`}>{icon}</span>
      <div>
        <p className={styles.checkPoint}>{check.point}</p>
        {check.reason ? <p className={styles.checkReason}>{check.reason}</p> : null}
      </div>
    </li>
  );
}

function AssetReport({
  asset,
  outdated,
  isRunning,
  onRerun,
}: {
  asset: IDraftingMediaAssets;
  outdated: boolean;
  isRunning: boolean;
  onRerun: (mediaAssetId: string) => void;
}) {
  const report = asset.complianceCheckReport;
  const pending = asset.complianceCheckStatus === "Pending";
  if (!report && !pending) return null;

  return (
    <article className={`${styles.report} ${outdated ? styles.reportOutdated : ""}`}>
      <div className={styles.reportHeader}>
        <h3 className={styles.reportTitle}>Findings</h3>
        {outdated ? <span className={styles.outdatedBadge}>Outdated</span> : null}
        {report ? (
          <span className={`${styles.statusPill} ${statusClass(report.status)}`}>
            {report.status || "Checked"}
          </span>
        ) : null}
      </div>
      {pending ? (
        <p className={styles.analyzing}>
          <Loader2 size={14} className="spin-animation" />
          Checking this asset…
        </p>
      ) : null}
      {report ? (
        <>
          <p className={styles.score}>Score {report.score}</p>
          {GROUPS.map((group) => {
            const items = report[group.key];
            if (!Array.isArray(items) || items.length === 0) return null;
            return (
              <div key={group.title} className={styles.checkGroup}>
                <h4 className={styles.checkGroupTitle}>{group.title}</h4>
                <ul className={styles.checkList}>
                  {items.map((check, index) => (
                    <CheckRow key={`${asset._id}-${group.title}-${index}`} check={check} />
                  ))}
                </ul>
              </div>
            );
          })}
        </>
      ) : null}
      {outdated ? (
        <div className={styles.outdatedBanner}>
          <p className={styles.outdatedCopy}>
            Request prompt changed. This asset check is outdated. Re-run it to update the review.
          </p>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={isRunning || !asset._id}
            onClick={() => onRerun(asset._id)}
          >
            <RefreshCw size={13} />
            Re-run asset check
          </button>
        </div>
      ) : null}
    </article>
  );
}

export function DraftMediaReports({
  assets,
  requestPromptId,
  promptChanged,
  isRunning,
  onRerun,
}: DraftMediaReportsProps) {
  const visible = assets.some(
    (asset) => asset.complianceCheckReport || asset.complianceCheckStatus === "Pending"
  );
  if (!visible && !isRunning) return null;

  return (
    <section className={styles.reports}>
      {assets.map((asset) => (
        <AssetReport
          key={asset._id || asset.url}
          asset={asset}
          outdated={assetOutdated(asset, requestPromptId, promptChanged)}
          isRunning={isRunning}
          onRerun={onRerun}
        />
      ))}
    </section>
  );
}
